import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Search, Plus, AlertTriangle, Clock, Package, Flag } from 'lucide-react';

interface Medication {
  medication_id: string;
  drug_name: string;
  generic_name: string;
  brand_name: string | null;
  ndc_code: string;
  strength: string;
  dosage_form: string;
  dea_schedule: string;
  is_controlled: boolean;
  lot_number: string | null;
  expiration_date: string | null;
  quantity_on_hand: number;
  unit_of_measure: string;
  par_level_min: number | null;
  par_level_max: number | null;
  unit_cost: number;
  storage_location: string;
  days_until_expiry: number | null;
  stock_status: string;
  needs_reorder: boolean;
  active_alerts_count: number;
  total_value: number;
}

interface Alert {
  alert_id: string;
  medication_id: string;
  alert_type: string;
  severity: string;
  message: string;
  flagged_for_reorder: boolean;
  resolved: boolean;
}

export default function MedicationsListRoute() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [stockFilter, setStockFilter] = useState<string>('all');
  const [expirationFilter, setExpirationFilter] = useState<string>('all');
  const [scheduleFilter, setScheduleFilter] = useState<string>('all');

  // Fetch medications
  const { data: medications = [], isLoading: medsLoading } = useQuery({
    queryKey: ['or-medications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('or_medications')
        .select('*')
        .order('drug_name');
      if (error) throw error;
      return data as Medication[];
    },
  });

  // Fetch alerts
  const { data: alerts = [] } = useQuery({
    queryKey: ['or-medication-alerts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('or_medication_alerts')
        .select('*')
        .eq('resolved', false)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Alert[];
    },
  });

  // Alert summaries
  const expiredCount = alerts.filter(a => a.alert_type === 'Expired').length;
  const expiringSoonCount = alerts.filter(a => a.alert_type === 'Expiring Soon').length;
  const belowParCount = alerts.filter(a => a.alert_type === 'Below Par').length;
  const reorderCount = alerts.filter(a => a.flagged_for_reorder).length;

  // Extract unique categories from dosage_form
  const categories = useMemo(() => {
    const cats = new Set(medications.map(m => m.dosage_form));
    return Array.from(cats).sort();
  }, [medications]);

  // Filter medications
  const filteredMedications = useMemo(() => {
    return medications.filter(med => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        med.drug_name.toLowerCase().includes(searchLower) ||
        med.generic_name?.toLowerCase().includes(searchLower) ||
        med.brand_name?.toLowerCase().includes(searchLower) ||
        med.ndc_code.toLowerCase().includes(searchLower) ||
        med.lot_number?.toLowerCase().includes(searchLower);

      // Category filter
      const matchesCategory = categoryFilter === 'all' || med.dosage_form === categoryFilter;

      // Stock status filter
      const matchesStock = stockFilter === 'all' || med.stock_status === stockFilter;

      // Expiration filter
      const matchesExpiration = expirationFilter === 'all' ||
        (expirationFilter === 'expired' && med.days_until_expiry !== null && med.days_until_expiry < 0) ||
        (expirationFilter === '<30' && med.days_until_expiry !== null && med.days_until_expiry >= 0 && med.days_until_expiry < 30) ||
        (expirationFilter === '<90' && med.days_until_expiry !== null && med.days_until_expiry >= 0 && med.days_until_expiry < 90) ||
        (expirationFilter === '>90' && (med.days_until_expiry === null || med.days_until_expiry >= 90));

      // DEA Schedule filter
      const matchesSchedule = scheduleFilter === 'all' ||
        (scheduleFilter === 'controlled' && med.is_controlled) ||
        (scheduleFilter === 'non-controlled' && !med.is_controlled) ||
        med.dea_schedule === scheduleFilter;

      return matchesSearch && matchesCategory && matchesStock && matchesExpiration && matchesSchedule;
    });
  }, [medications, searchTerm, categoryFilter, stockFilter, expirationFilter, scheduleFilter]);

  // Summary stats
  const totalValue = filteredMedications.reduce((sum, m) => sum + m.total_value, 0);
  const controlledCount = filteredMedications.filter(m => m.is_controlled).length;

  const getStockBadgeColor = (status: string) => {
    switch (status) {
      case 'Out of Stock': return 'bg-pt-red/20 text-pt-red border-pt-red/30';
      case 'Low Stock': return 'bg-pt-amber/20 text-pt-amber border-pt-amber/30';
      case 'Overstocked': return 'bg-pt-cyan/20 text-pt-cyan border-pt-cyan/30';
      default: return 'bg-pt-green/20 text-pt-green border-pt-green/30';
    }
  };

  const getScheduleBadgeColor = (schedule: string) => {
    switch (schedule) {
      case 'Schedule II': return 'bg-pt-red/20 text-pt-red border-pt-red/30';
      case 'Schedule III': return 'bg-pt-orange/20 text-pt-orange border-pt-orange/30';
      case 'Schedule IV': return 'bg-pt-amber/20 text-pt-amber border-pt-amber/30';
      case 'Schedule V': return 'bg-pt-cyan/20 text-pt-cyan border-pt-cyan/30';
      default: return 'bg-pt-text-dim/20 text-pt-text border-pt-input-border';
    }
  };

  if (medsLoading) {
    return (
      <div className="min-h-full bg-pt-darker flex items-center justify-center">
        <div className="text-pt-cyan text-lg">Loading medications...</div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-pt-darker text-pt-text p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-pt-text mb-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            Medication Inventory
          </h1>
          <p className="text-pt-text-muted text-sm">OR medication tracking with DEA compliance</p>
        </div>
        <button
          onClick={() => navigate('/medications/new')}
          className="px-4 py-2 bg-pt-gold hover:bg-pt-gold/80 text-pt-darker rounded-lg font-semibold flex items-center gap-2 transition-colors"
        >
          <Plus size={18} />
          New Medication
        </button>
      </div>

      {/* Alert Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-pt-dark/50 border border-pt-red/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="text-pt-red" size={20} />
            <span className="text-2xl font-bold text-pt-red">{expiredCount}</span>
          </div>
          <div className="text-sm text-pt-text">Expired Medications</div>
        </div>

        <div className="bg-pt-dark/50 border border-pt-amber/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Clock className="text-pt-amber" size={20} />
            <span className="text-2xl font-bold text-pt-amber">{expiringSoonCount}</span>
          </div>
          <div className="text-sm text-pt-text">Expiring Soon (&lt;30d)</div>
        </div>

        <div className="bg-pt-dark/50 border border-pt-orange/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Package className="text-pt-orange" size={20} />
            <span className="text-2xl font-bold text-pt-orange">{belowParCount}</span>
          </div>
          <div className="text-sm text-pt-text">Below Par Level</div>
        </div>

        <div className="bg-pt-dark/50 border border-pt-cyan/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Flag className="text-pt-cyan" size={20} />
            <span className="text-2xl font-bold text-pt-cyan">{reorderCount}</span>
          </div>
          <div className="text-sm text-pt-text">Flagged for Reorder</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-pt-text-muted" size={18} />
              <input
                type="text"
                placeholder="Search medications, NDC, lot number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text placeholder-pt-text0 focus:outline-none focus:border-pt-cyan/50"
              />
            </div>
          </div>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
          >
            <option value="all">All Forms</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Stock Filter */}
          <select
            value={stockFilter}
            onChange={(e) => setStockFilter(e.target.value)}
            className="px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
          >
            <option value="all">All Stock Levels</option>
            <option value="Out of Stock">Out of Stock</option>
            <option value="Low Stock">Low Stock</option>
            <option value="In Stock">In Stock</option>
            <option value="Overstocked">Overstocked</option>
          </select>

          {/* Expiration Filter */}
          <select
            value={expirationFilter}
            onChange={(e) => setExpirationFilter(e.target.value)}
            className="px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
          >
            <option value="all">All Expirations</option>
            <option value="expired">Expired</option>
            <option value="<30">&lt;30 days</option>
            <option value="<90">&lt;90 days</option>
            <option value=">90">&gt;90 days</option>
          </select>
        </div>

        {/* DEA Schedule Filter Row */}
        <div className="mt-4">
          <select
            value={scheduleFilter}
            onChange={(e) => setScheduleFilter(e.target.value)}
            className="px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
          >
            <option value="all">All DEA Schedules</option>
            <option value="controlled">All Controlled</option>
            <option value="non-controlled">Non-Controlled</option>
            <option value="Schedule II">Schedule II</option>
            <option value="Schedule III">Schedule III</option>
            <option value="Schedule IV">Schedule IV</option>
            <option value="Schedule V">Schedule V</option>
          </select>
        </div>
      </div>

      {/* Medications Table */}
      <div className="bg-pt-dark/30 border border-pt-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-pt-darker/50 border-b border-pt-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase tracking-wider">
                  Medication
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase tracking-wider">
                  NDC
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase tracking-wider">
                  Strength
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase tracking-wider">
                  Form
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase tracking-wider">
                  DEA Schedule
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase tracking-wider">
                  Lot
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase tracking-wider">
                  Expiration
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase tracking-wider">
                  Location
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-pt-border">
              {filteredMedications.map((med) => (
                <tr
                  key={med.medication_id}
                  onClick={() => navigate(`/medications/${med.medication_id}`)}
                  className="hover:bg-pt-panel/20 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {med.active_alerts_count > 0 && (
                        <AlertTriangle size={14} className="text-pt-red" />
                      )}
                      <div>
                        <div className="text-pt-text font-medium">{med.drug_name}</div>
                        {med.brand_name && (
                          <div className="text-xs text-pt-text-muted">({med.brand_name})</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-pt-text text-sm font-mono">{med.ndc_code}</td>
                  <td className="px-4 py-3 text-pt-text text-sm">{med.strength}</td>
                  <td className="px-4 py-3 text-pt-text text-sm">{med.dosage_form}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getScheduleBadgeColor(med.dea_schedule)}`}>
                      {med.is_controlled ? med.dea_schedule.replace('Schedule ', 'Sched ') : 'Non-Ctrl'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-pt-text text-sm font-mono">{med.lot_number || '—'}</td>
                  <td className="px-4 py-3">
                    {med.expiration_date ? (
                      <div>
                        <div className="text-pt-text text-sm">
                          {new Date(med.expiration_date).toLocaleDateString()}
                        </div>
                        {med.days_until_expiry !== null && (
                          <div className={`text-xs ${
                            med.days_until_expiry < 0 ? 'text-pt-red' :
                            med.days_until_expiry < 30 ? 'text-pt-amber' :
                            med.days_until_expiry < 90 ? 'text-pt-cyan' :
                            'text-pt-text0'
                          }`}>
                            {med.days_until_expiry < 0 
                              ? `Expired ${Math.abs(med.days_until_expiry)}d ago`
                              : `${med.days_until_expiry}d remaining`
                            }
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-pt-text0">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-pt-text font-medium">{med.quantity_on_hand}</div>
                    <div className="text-xs text-pt-text-muted">{med.unit_of_measure}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStockBadgeColor(med.stock_status)}`}>
                      {med.stock_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-pt-text text-sm">{med.storage_location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Summary Footer */}
        <div className="bg-pt-darker/50 border-t border-pt-border px-4 py-3 flex items-center justify-between">
          <div className="text-sm text-pt-text-muted">
            Showing <span className="text-pt-text font-medium">{filteredMedications.length}</span> of{' '}
            <span className="text-pt-text font-medium">{medications.length}</span> medications
            {controlledCount > 0 && (
              <span className="ml-3 text-pt-red">
                • {controlledCount} controlled substances
              </span>
            )}
          </div>
          <div className="text-sm text-pt-text-muted">
            Total Value: <span className="text-pt-gold font-semibold">${totalValue.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
